import React, { useRef, useLayoutEffect } from 'react';
import { Avatar, Typography, IconButton, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import Loading from '../../../components/loading/Loading';
import MessageBubble from './MessageBubble';
import Composer from './Composer';
import ContactContextBar from './ContactContextBar';
import { formatPhoneNumber, formatDayLabel, getChatKey, getSendTarget } from '../utils';
import styles from './ChatWindow.module.css';

const NEAR_BOTTOM_THRESHOLD = 120; // px do fim para considerar "no fim da conversa"
const LOAD_OLDER_THRESHOLD = 60;   // px do topo para carregar página anterior

/**
 * Coluna direita: cabeçalho da conversa, mensagens (com separadores de
 * data, scroll infinito para cima e auto-scroll inteligente) e o Composer.
 */
const ChatWindow = ({
  chat,
  messages,
  loading,
  loadingOlder,
  hasMore,
  onLoadOlder,
  onSend,
  onRetry,
  onDiscard,
  onRequestAISuggestion,
  onSuggestionOutcome,
  isMobileView,
  onBack,
}) => {
  const chatKey = getChatKey(chat);

  // Maior id persistido na conversa: a sugestão da IA guarda o id que viu e o
  // Composer avisa quando chegou mensagem nova depois dela.
  const lastMessageId = messages.reduce((max, msg) => {
    const id = Number(msg.id);
    return Number.isFinite(id) && id > max ? id : max;
  }, 0);

  const containerRef = useRef(null);
  const nearBottomRef = useRef(true);
  const pendingScrollAdjustRef = useRef(null);
  const prevChatRef = useRef(null);
  const prevLastIdRef = useRef(null);

  const scrollToBottom = (smooth) => {
    const el = containerRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  };

  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;

    nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_THRESHOLD;

    if (el.scrollTop < LOAD_OLDER_THRESHOLD && hasMore && !loadingOlder && !loading) {
      // Guarda a posição para restaurar depois que a página anterior chegar
      pendingScrollAdjustRef.current = { scrollHeight: el.scrollHeight, scrollTop: el.scrollTop };
      onLoadOlder();
    }
  };

  // Gerencia a posição do scroll a cada mudança na lista de mensagens
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || messages.length === 0) return;

    const chatChanged = prevChatRef.current !== chatKey;
    prevChatRef.current = chatKey;
    const lastId = messages[messages.length - 1]?.messageId;

    if (chatChanged) {
      // Conversa recém-aberta: vai direto para o fim
      scrollToBottom(false);
      prevLastIdRef.current = lastId;
      nearBottomRef.current = true;
      return;
    }

    if (pendingScrollAdjustRef.current) {
      // Página anterior foi prependada: mantém o usuário no mesmo ponto
      const { scrollHeight, scrollTop } = pendingScrollAdjustRef.current;
      el.scrollTop = el.scrollHeight - scrollHeight + scrollTop;
      pendingScrollAdjustRef.current = null;
      return;
    }

    if (lastId !== prevLastIdRef.current) {
      // Mensagem nova no fim: acompanha se o usuário já estava perto do fim
      // (mensagens enviadas pelo próprio painel sempre acompanham)
      const last = messages[messages.length - 1];
      prevLastIdRef.current = lastId;
      if ((last.direction === 'sent' && last.sendStatus) || nearBottomRef.current) {
        scrollToBottom(true);
      }
    }
  }, [messages, chatKey]);

  if (!chat) {
    return (
      <div className={styles.emptyState}>
        <WhatsAppIcon className={styles.emptyIcon} />
        <Typography variant="h6" className={styles.emptyTitle}>
          Selecione uma conversa
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Escolha um paciente na lista ao lado para ver e responder as mensagens.
        </Typography>
      </div>
    );
  }

  // Monta a lista com separadores de data
  let lastDay = null;
  const items = messages.map((msg, index) => {
    const day = new Date(msg.date).toDateString();
    const showSeparator = day !== lastDay;
    lastDay = day;

    return (
      <React.Fragment key={msg.messageId || index}>
        {showSeparator && (
          <div className={styles.daySeparator}>
            <span>{formatDayLabel(msg.date)}</span>
          </div>
        )}
        <MessageBubble msg={msg} onRetry={onRetry} onDiscard={onDiscard} />
      </React.Fragment>
    );
  });

  return (
    <div className={styles.container}>
      {/* Cabeçalho + contexto do contato ocupam a mesma linha do grid, para
          a área de mensagens continuar sendo a única que encolhe/rola */}
      <div className={styles.headerArea}>
        <div className={styles.header}>
          {isMobileView && (
            <IconButton onClick={onBack} size="small">
              <ArrowBackIcon />
            </IconButton>
          )}
          <Avatar src={chat.chatPhoto} alt={chat.chatName} sx={{ width: 44, height: 44 }} />
          <div className={styles.headerInfo}>
            <Typography variant="subtitle1" className={styles.headerName}>
              {chat.chatName || formatPhoneNumber(getSendTarget(chat)) || 'Contato WhatsApp'}
            </Typography>
            {formatPhoneNumber(getSendTarget(chat)) && (
              <Typography variant="body2" color="textSecondary" className={styles.headerPhone}>
                {formatPhoneNumber(getSendTarget(chat))}
              </Typography>
            )}
          </div>
        </div>

        <ContactContextBar chat={chat} />
      </div>

      <div ref={containerRef} className={styles.messages} onScroll={handleScroll}>
        {loading ? (
          <div className={styles.loadingWrapper}><Loading /></div>
        ) : (
          <>
            {loadingOlder && (
              <div className={styles.loadingOlder}>
                <CircularProgress size={20} />
              </div>
            )}
            {items}
          </>
        )}
      </div>

      <Composer
        onSend={onSend}
        onRequestAISuggestion={onRequestAISuggestion}
        onSuggestionOutcome={onSuggestionOutcome}
        disabled={loading}
        chatKey={chatKey}
        lastMessageId={lastMessageId}
      />
    </div>
  );
};

export default ChatWindow;
